import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ClerkProvider,
  Show,
  SignIn,
  useOrganization,
  useSession,
  useUser,
  useClerk,
} from '@clerk/react';
import { esES } from '@clerk/localizations';

import './index.css';
import 'react-tooltip/dist/react-tooltip.css';
import Main from './routes/main';
import Spinner from './components/Spinner';

const clerkAppearance = { variables: { colorPrimary: '#2A64DF' } };

const centeredScreen =
  'flex h-screen w-screen items-center justify-center bg-zinc-50';

function Home() {
  return (
    <div className="h-full bg-zinc-50">
      <main className="flex flex-col h-full w-full py-12 px-10 gap-4">
        <nav
          className="absolute flex items-center justify-between"
          aria-label="Global"
        >
          <a href="https://app.swipoo.com" target="_blank" rel="noreferrer">
            <span className="sr-only">Swipoo.com</span>
            <img
              alt="logo"
              className="h-8 w-auto sm:h-10"
              src="https://elreferente.es/wp-content/uploads/2022/01/Logo_swipoo.png"
            />
          </a>
        </nav>
        <section className="flex flex-col lg:flex-row items-center justify-center lg:justify-around gap-12 lg:gap-20 h-full">
          <h1 className="flex flex-col items-center lg:items-start gap-1 text-4xl md:text-6xl 2xl:text-7xl tracking-tight font-extrabold text-gray-900">
            Gestoría online
            <span className="text-center text-blue-600">de automoción</span>
          </h1>
          <SignIn routing="hash" appearance={clerkAppearance} />
        </section>
      </main>
    </div>
  );
}

function NoOrganizationScreen() {
  const { signOut } = useClerk();

  return (
    <div className={centeredScreen}>
      <div className="mx-4 max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          No tienes acceso a ninguna organización
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          Tu cuenta no pertenece a ninguna organización. Pide al administrador
          de Swipoo que te añada a una para poder acceder.
        </p>
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-6 rounded-md bg-[#2A64DF] px-4 py-2 text-sm font-medium text-white hover:bg-[#1f4fc0]"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

const ORG_CHOSEN_SESSION_KEY = 'swipoo-org-chosen-for-session';

function ChooseOrganizationScreen({ onChosen }) {
  const { setActive } = useClerk();
  const { user } = useUser();
  const [isActivating, setIsActivating] = useState(false);

  const chooseOrganization = async (organizationId) => {
    setIsActivating(true);
    try {
      await setActive({ organization: organizationId });
      onChosen();
    } finally {
      setIsActivating(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className={centeredScreen}>
      <div className="mx-4 w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          Elige una organización
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Selecciona con qué organización quieres acceder.
        </p>
        <ul className="mt-6 flex flex-col gap-2">
          {user.organizationMemberships.map(({ organization }) => (
            <li key={organization.id}>
              <button
                type="button"
                disabled={isActivating}
                onClick={() => chooseOrganization(organization.id)}
                className="flex w-full items-center gap-3 rounded-md border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-900 hover:border-[#2A64DF] hover:bg-blue-50 disabled:opacity-50"
              >
                <img
                  src={organization.imageUrl}
                  alt="logo"
                  className="h-8 w-8 rounded-md"
                />
                {organization.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { user } = useUser();
  const { session } = useSession();
  const { setActive } = useClerk();
  const { organization } = useOrganization();
  const task = session?.currentTask;
  const [orgChosen, setOrgChosen] = useState(
    () => localStorage.getItem(ORG_CHOSEN_SESSION_KEY) === session?.id,
  );

  const markOrgChosen = () => {
    localStorage.setItem(ORG_CHOSEN_SESSION_KEY, session.id);
    setOrgChosen(true);
  };

  const onlyOrganizationId =
    user && user.organizationMemberships.length === 1
      ? user.organizationMemberships[0].organization.id
      : null;
  const mustActivateOnlyOrganization =
    Boolean(onlyOrganizationId) && !organization;

  useEffect(() => {
    if (mustActivateOnlyOrganization) {
      setActive({ organization: onlyOrganizationId });
    }
  }, [mustActivateOnlyOrganization, onlyOrganizationId]);

  if (user && user.organizationMemberships.length === 0) {
    return <NoOrganizationScreen />;
  }

  const mustChooseOrganization =
    task?.key === 'choose-organization' ||
    (user && user.organizationMemberships.length > 1 && !orgChosen);

  if (mustChooseOrganization && !onlyOrganizationId) {
    return <ChooseOrganizationScreen onChosen={markOrgChosen} />;
  }

  if (mustActivateOnlyOrganization) {
    return (
      <div className={centeredScreen}>
        <Spinner color="text-blue-700" size={10} />
      </div>
    );
  }

  if (task) {
    return (
      <div className={centeredScreen}>
        <SignIn routing="hash" appearance={clerkAppearance} />
      </div>
    );
  }

  return <Main />;
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ClerkProvider
      localization={esES}
      publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}
    >
      <Show when="signed-out" treatPendingAsSignedOut={false}>
        <Home />
      </Show>
      <Show when="signed-in" treatPendingAsSignedOut={false}>
        <AuthenticatedApp />
      </Show>
    </ClerkProvider>
  </React.StrictMode>,
);
